type Execution = { command?: unknown; status?: unknown; exitCode?: unknown };
type RecordValue = Record<string, unknown>;
const object = (value: unknown): RecordValue | undefined => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as RecordValue : undefined;
const workspacePath = (value: unknown): value is string => typeof value === 'string' && value.startsWith('/workspace/') && !/[\x00-\x1f\x7f\\]/.test(value) && value.slice(1).split('/').every(part => part !== '' && part !== '.' && part !== '..');

/** Conservative shell word decoding; operators and substitutions are not commands. */
function shellWords(command: string): string[] | undefined {
  if (/[\x00\r\n]/.test(command)) return;
  const words: string[] = [];
  let word = '', quote: '' | '"' | "'" = '', started = false;
  for (let index = 0; index < command.length; index++) {
    const character = command[index];
    if (quote === "'") { if (character === "'") quote = ''; else word += character; continue; }
    if (character === '\\') {
      const next = command[++index]; if (next === undefined) return;
      if (quote === '"' && !['"', '\\', '$', '`'].includes(next)) word += '\\';
      word += next; started = true; continue;
    }
    if (quote === '"') {
      if (character === '"') quote = '';
      else if (character === '$' || character === '`') return;
      else word += character;
      continue;
    }
    if (character === "'" || character === '"') { quote = character; started = true; continue; }
    if (/\s/.test(character)) { if (started) { words.push(word); word = ''; started = false; } continue; }
    if (/[;&|<>(){}$`#]/.test(character)) return;
    word += character; started = true;
  }
  if (quote) return;
  if (started) words.push(word);
  return words;
}

function isPdfExtractCommand(command: unknown): boolean {
  if (typeof command !== 'string') return false;
  let words = shellWords(command); if (!words) return false;
  if (['bash', 'sh', 'zsh', '/bin/bash', '/bin/sh', '/bin/zsh', '/usr/bin/bash', '/usr/bin/sh', '/usr/bin/zsh'].includes(words[0])) {
    if (words.length !== 3 || !['-c', '-lc'].includes(words[1])) return false;
    words = shellWords(words[2]); if (!words) return false;
  }
  return words.length >= 3 && /^(?:pdf|\/(?:[A-Za-z0-9._-]+\/)*pdf)$/.test(words[0]) && words[1] === 'extract';
}

/** A display projection only. A matching marker does not prove model ingestion. */
export function pdfExtractDisplayOutput(output: string, execution: Execution): string {
  if (execution.exitCode !== 0 || !['completed', 'complete', 'success'].includes(String(execution.status)) || !isPdfExtractCommand(execution.command)) return output;
  return extractSummaryDisplayOutput(output);
}

/** The complete normal CLI text summary is the only removable-marker format. */
function extractSummaryDisplayOutput(output: string): string {
  const body = output.replace(/\r?\n$/, ''), lines = body.split(/\r?\n/);
  if (lines.length !== 6 && lines.length !== 8) return output;
  const heading = lines[0].match(/^Extracted PDF pages from ("(?:[^"\\\r\n]|\\.)*")$/), sourceCount = lines[1].match(/^sourcePageCount: ([1-9]\d*)$/);
  if (!heading || !sourceCount) return output;
  let sourcePath: unknown;
  try { sourcePath = JSON.parse(heading[1]); } catch { return output; }
  const pageCount = Number(sourceCount[1]);
  if (!workspacePath(sourcePath) || !Number.isSafeInteger(pageCount)) return output;
  let offset = 2, physicalCount: number | undefined;
  if (lines.length === 8) {
    if (lines[offset++] !== 'pageSelection: physical PDF pages (not visible book page labels)') return output;
    const ranges = lines[offset++].match(/^extractedPDFPages: (PDF [1-9]\d*(?:-[1-9]\d*)?(?:, PDF [1-9]\d*(?:-[1-9]\d*)?)*)$/);
    if (!ranges) return output;
    physicalCount = 0;
    let previousEnd = 0;
    for (const range of ranges[1].split(', ')) {
      const [start, end = start] = range.slice(4).split('-').map(Number);
      if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start <= previousEnd || start > end || end > pageCount) return output;
      physicalCount += end - start + 1; previousEnd = end;
      if (physicalCount > 80) return output;
    }
  }
  if (!/^extractedRanges: [^\r\n]+$/.test(lines[offset++])) return output;
  const count = lines[offset++].match(/^extractedPageCount: ([1-9]\d*)$/);
  if (!count || !/^warnings: [^\r\n]+$/.test(lines[offset++])) return output;
  const extractedCount = Number(count[1]);
  if (!Number.isSafeInteger(extractedCount) || extractedCount > pageCount || extractedCount > 80 || (physicalCount !== undefined && physicalCount !== extractedCount)) return output;
  let marker: RecordValue | undefined;
  try { marker = object(JSON.parse(lines[offset])); } catch { return output; }
  if (!marker || Object.keys(marker).length !== 1 || !Object.hasOwn(marker, 'readosPdfInput')) return output;
  const reference = object(marker.readosPdfInput);
  if (!reference || Object.keys(reference).length !== 3 || !['version', 'manifestPath', 'manifestSha256'].every(key => Object.hasOwn(reference, key)) || reference.version !== 2 || !workspacePath(reference.manifestPath) || typeof reference.manifestSha256 !== 'string' || !/^[0-9a-f]{64}$/.test(reference.manifestSha256)) return output;
  return output.slice(0, body.lastIndexOf('\n') + 1);
}
import type { PdfExtractPreview } from '@reados/contracts';


/** Exact verified sideband permits the same display treatment after unrelated
 * preceding output. Preserve every preceding byte, and never clean JSON mode. */
export function verifiedPdfExtractDisplayOutput(output: string, execution: Execution, preview: PdfExtractPreview): string {
  if (execution.exitCode !== 0 || !['completed', 'complete', 'success'].includes(String(execution.status))
    || typeof execution.command !== 'string') return output;
  const body = output.replace(/\r?\n$/, ''), lines = body.split(/\r?\n/);
  for (const length of [6, 8]) {
    if (lines.length < length) continue;
    const tail = lines.slice(-length), suffix = tail.join('\n') + '\n';
    if (extractSummaryDisplayOutput(suffix) === suffix) continue;
    // The shared summary validator has already established the exact shapes.
    const marker = JSON.parse(tail.at(-1)!).readosPdfInput;
    const sourcePath = JSON.parse(tail[0].slice('Extracted PDF pages from '.length));
    const count = Number(tail[length - 3].slice('extractedPageCount: '.length));
    if (marker.version !== 2 || marker.manifestPath !== preview.manifestPath || marker.manifestSha256 !== preview.manifestSha256
      || sourcePath !== preview.source.path || count !== preview.pages.length) continue;
    if (length === 8) {
      const physicalPages = tail[3].slice('extractedPDFPages: '.length).split(', ').flatMap(range => {
        const [first, last = first] = range.slice(4).split('-').map(Number);
        return Array.from({ length: last - first + 1 }, (_, index) => first + index);
      });
      if (physicalPages.length !== preview.pages.length || physicalPages.some((page, index) => page !== preview.pages[index].physicalPage)) continue;
    }
    return output.slice(0, body.lastIndexOf('\n') + 1);
  }
  return output;
}
